import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
    TypographyProps,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { usePostB2BMutation, usePutB2BMutation } from '@/api/v2/b2b';
import { B2B, B2BUserWithCredentials } from '@/models/b2b';
import { BoldLabel } from '@starjack/shared-components';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';

const typoGraphyStyling: TypographyProps['sx'] = {
    width: '100%',
};

type B2BDrawerProps = {
    onClose: () => void;
    b2b: B2BUserWithCredentials;
};

export const B2BDrawer: FunctionComponent<B2BDrawerProps> = ({
    onClose,
    b2b,
}) => {
    const mode = b2b.id ? 'edit' : 'new';
    const [localB2B, setLocalB2B] = useState<B2BUserWithCredentials>(b2b);
    const [showInputError, setShowInputError] = useState(false);
    const [resetPassword, setResetPassword] = useState(false);
    useDirtyGuard(!isEqual(localB2B, b2b) || resetPassword);

    const [postB2B, { isLoading: postB2BLoading }] = usePostB2BMutation();
    const [putB2B, { isLoading: putB2BLoading }] = usePutB2BMutation();
    const { addNotification } = useNotification();
    const [showPasswordDialog, setShowPasswordDialog] = useState<string>(null);

    const onSave = () => {
        if (!localB2B.name) {
            setShowInputError(true);
            return;
        }

        const action = mode === 'edit' ? putB2B : postB2B;
        const args =
            mode === 'edit' ? { resetPassword, ...localB2B } : localB2B;

        action(args)
            .unwrap()
            .then((newB2B) => {
                addNotification({
                    type: 'success',
                    message: 'B2B erfolgreich gespeichert',
                });

                if (newB2B.password) {
                    setLocalB2B(newB2B);
                    setShowPasswordDialog(newB2B.password);
                } else {
                    onClose();
                }
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                });
            });
    };

    const onInputChange =
        (key: keyof B2B) => (e: ChangeEvent<HTMLInputElement>) => {
            const { value, checked } = e.target;

            let nextValue: string | boolean = value;
            if (key === 'enabled') {
                nextValue = checked;
            }
            if (key === 'beiAutoDepot') {
                nextValue = checked;
            }

            if (key === 'name' && nextValue) {
                setShowInputError(false);
            }

            setLocalB2B((prevState) => ({
                ...prevState,
                [key]: nextValue,
            }));
        };

    useEffect(() => {
        setLocalB2B(b2b);
    }, [b2b]);

    return (
        <Box sx={{ width: '400px' }}>
            <Box
                sx={{
                    bgcolor: 'primary.dark',
                    color: '#fff',
                    padding: 4,
                }}
            >
                <Typography variant='h6'>
                    {mode === 'edit'
                        ? 'B2B-Partner bearbeiten'
                        : 'Neuen B2B-Partner anlegen'}
                </Typography>
            </Box>

            <Stack spacing={3} sx={{ p: 4 }}>
                <TextField
                    sx={{ ...typoGraphyStyling }}
                    onChange={onInputChange('name')}
                    value={localB2B.name}
                    label='Name der B2B Partnerfirma'
                    error={showInputError}
                    helperText={
                        showInputError
                            ? 'Bitte einen Benutzernamen angeben'
                            : ''
                    }
                    required
                />
                <TextField
                    sx={{ ...typoGraphyStyling }}
                    value={localB2B.beiUsername}
                    onChange={onInputChange('beiUsername')}
                    label='BEI Benutzername'
                />
                <TextField
                    sx={{ ...typoGraphyStyling }}
                    label='BEI Passwort'
                    value={localB2B.beiPassword}
                    onChange={onInputChange('beiPassword')}
                />

                {!!b2b.id && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={resetPassword}
                                onChange={() => {
                                    setResetPassword((prevState) => !prevState);
                                }}
                            />
                        }
                        label='Neues Passwort'
                    />
                )}

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={localB2B.enabled}
                            onChange={onInputChange('enabled')}
                        />
                    }
                    label='Aktiviert'
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={localB2B.beiAutoDepot}
                            onChange={onInputChange('beiAutoDepot')}
                        />
                    }
                    label='Auto Depot'
                />

                <LoadingButton
                    sx={{ ...typoGraphyStyling }}
                    variant='contained'
                    onClick={onSave}
                    loading={postB2BLoading || putB2BLoading}
                >
                    Speichern
                </LoadingButton>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <Button
                        sx={{
                            width: 200,
                        }}
                        onClick={onClose}
                        variant='outlined'
                    >
                        Abbrechen
                    </Button>
                </Box>
            </Stack>

            <Dialog
                onClose={() => {
                    setShowPasswordDialog(null);
                    onClose();
                }}
                open={!!showPasswordDialog}
            >
                <DialogTitle>B2B-Passwort</DialogTitle>
                <DialogContent>
                    Das Passwort für die angelegte B2B-Stelle lautet:{' '}
                    <BoldLabel>{localB2B.password}</BoldLabel>. Bitte an einem
                    sicheren Ort speichern. Das Passwort kann später nicht mehr
                    aufgerufen werden.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowPasswordDialog(null);
                            onClose();
                        }}
                    >
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
